import buildProviderParams, { providerParamsReset } from './cta-params';

// HOME-9598: fire provider_impression once when a provider tile's CTA/TFN first
// becomes visible. Payload mirrors cta_click_out (shared via cta-params.js).
//
// Some tiles' CTA arrives late — e.g. the promotion-scheduling plugin swaps a
// tile's default CTA for a sale "Get Deal" button (or adds a whole new promo
// tile) via an AJAX call that resolves after our initial DOM sweep, removing the
// old anchor in the process. A MutationObserver picks up anchors added later so
// those tiles are tracked too, without double-counting a tile that already fired
// under a previous anchor.

const CTA_SELECTOR = '[data-ga="referral-cta"]';
const TFN_SELECTOR = '[data-ga="referral-call"]';
const CTA_OR_TFN_SELECTOR = `${CTA_SELECTOR}, ${TFN_SELECTOR}`;
const EVENT = 'provider_impression';
const NON_PROVIDER_CTA_SELECTOR = '.quiz-cta-btn';
// Stall-guard only: releases a gate if GTM is present but a tag never completes
// firing. Must be long enough to never trip during a normal GA4 cold start —
// otherwise it re-introduces the "last value wins" race across tiles visible at
// once (same rationale as providers.js, HOME-9225).
const TAG_SETTLE_STALL_GUARD_MS = 8000;

// True once the GTM container has loaded. When GTM is absent (blocked/ad-blocked)
// eventCallback never fires, so we must not wait on it.
function gtmPresent() {
    return typeof window.google_tag_manager !== 'undefined';
}

// Placement is part of the identity so the same provider tracks separately per
// section (top-3 cards, comparison table, detailed breakdown, etc). A CTA and a
// TFN in the same tile still collapse to one impression since they share all three.
function tileKey(element) {
    const position = element.dataset.order || 'single';
    const serviceProvider = element.dataset.productname || 'no productname';
    const placement = element.dataset.elementplacement || 'no placement';
    return `${position}:${serviceProvider}:${placement}`;
}

function isProviderAnchor(anchor) {
    return !anchor.matches(NON_PROVIDER_CTA_SELECTOR);
}

// One anchor per tile, preferring the CTA so the payload matches cta_click_out.
// Non-provider anchors are dropped before the dedupe so they can never occupy a
// tile's slot in the map and starve the real anchor behind them.
function representativeAnchors() {
    const byKey = new Map();

    document.querySelectorAll(CTA_OR_TFN_SELECTOR).forEach((anchor) => {
        if (!isProviderAnchor(anchor)) {
            return;
        }

        const key = tileKey(anchor);
        const current = byKey.get(key);
        const isCta = anchor.dataset.ga === 'referral-cta';

        if (!current || (isCta && current.dataset.ga !== 'referral-cta')) {
            byKey.set(key, anchor);
        }
    });

    return [...byKey.values()];
}

function buildPayload(anchor) {
    const isPhone = anchor.dataset.ga === 'referral-call';

    return {
        event: EVENT,
        elementClicked: isPhone ? 'phone link' : 'cta button',
        destinationType: isPhone ? 'provider' : (anchor.dataset.destination || 'generic'),
        ...buildProviderParams(anchor),
    };
}

// Gate on eventCallback so bursts of tiles don't overwrite each other's shared
// DLV keys before GA4 reads them (same fix as provider_listing_position, HOME-9225).
function pushEvent(payload) {
    return new Promise((resolve) => {
        let settled = false;
        const done = () => {
            if (settled) {
                return;
            }
            settled = true;
            resolve();
        };

        window.dataLayer = window.dataLayer || [];

        // GTM absent/blocked: nothing will ever read these keys or call
        // eventCallback, so push and move on immediately instead of waiting.
        if (!gtmPresent()) {
            window.dataLayer.push({ ...payload });
            setTimeout(done, 0);
            return;
        }

        window.dataLayer.push({
            ...payload,
            // Deliberately NO eventTimeout: it would make GTM invoke eventCallback
            // early on a cold GA4 start, before the tag reads these DLV keys, and
            // re-introduce the "last value wins" race (HOME-9225).
            eventCallback: done,
        });

        setTimeout(done, TAG_SETTLE_STALL_GUARD_MS);
    });
}

// The DLV keys this event sets are shared with cta_click_out and are "last value
// wins", so clear them once the tag has read them. Without this a later unrelated
// event (an internal link click, site_search) inherits this tile's
// serviceProvider / partner / position / etc. -- the HOME-9595 leak, on the
// impression producer. Safe to push here because pushEvent() has already resolved,
// meaning GTM called eventCallback (or the stall guard gave up waiting).
function clearProviderParams() {
    window.dataLayer.push(providerParamsReset());
}

// Serialise pushes so bursts settle one at a time.
let queue = Promise.resolve();

function enqueue(payload) {
    queue = queue.then(() => pushEvent(payload)).then(clearProviderParams);
}

function whenReady() {
    const pageLoaded = document.readyState === 'complete'
        ? Promise.resolve()
        : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));

    return pageLoaded.then(() => window.soGoogleDataFetcher?.analyticsPromise);
}

// Identity-based, not node-based: a tile only ever fires once, no matter how many
// different anchor elements end up representing it (e.g. a sale CTA swapped in
// after the fact). observedAnchors just guards against handing the same node to
// the IntersectionObserver twice.
const firedTileKeys = new Set();
const observedAnchors = new WeakSet();
let intersectionObserver = null;

function onIntersect(entries) {
    entries.forEach((entry) => {
        if (!entry.isIntersecting) {
            return;
        }

        const anchor = entry.target;
        const key = tileKey(anchor);

        intersectionObserver.unobserve(anchor);

        if (firedTileKeys.has(key)) {
            return;
        }

        firedTileKeys.add(key);
        enqueue(buildPayload(anchor));
    });
}

function trackAnchor(anchor) {
    if (!isProviderAnchor(anchor)) {
        return;
    }

    const key = tileKey(anchor);

    if (firedTileKeys.has(key) || observedAnchors.has(anchor)) {
        return;
    }

    observedAnchors.add(anchor);

    // No IntersectionObserver support: can't tell what's visible, so fire now.
    if (!intersectionObserver) {
        firedTileKeys.add(key);
        enqueue(buildPayload(anchor));
        return;
    }

    intersectionObserver.observe(anchor);
}

// Catches CTAs/TFNs that show up after the initial sweep — e.g. the
// promotion-scheduling plugin injecting a sale CTA (or a whole new promo tile)
// once its AJAX call resolves.
function watchForNewAnchors() {
    if (!('MutationObserver' in window)) {
        return;
    }

    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType !== Node.ELEMENT_NODE) {
                    return;
                }

                const anchors = node.matches(CTA_OR_TFN_SELECTOR)
                    ? [node]
                    : [...node.querySelectorAll(CTA_OR_TFN_SELECTOR)];

                anchors.forEach(trackAnchor);
            });
        });
    }).observe(document.body, { childList: true, subtree: true });
}

export default function init() {
    if (window.soProviderImpressionsTracked) {
        return;
    }

    window.soProviderImpressionsTracked = true;

    whenReady().then(() => {
        if ('IntersectionObserver' in window) {
            intersectionObserver = new IntersectionObserver(onIntersect);
        }

        representativeAnchors().forEach(trackAnchor);
        watchForNewAnchors();
    });
}
